// Pure restructuring of a raw ChatGPT answer for the free report's live test.
// It NEVER rewrites wording: it only splits the text into blocks and strips
// markdown / URL syntax (links become source domains). No JSX, no DOM.
//
// Known limits (no words are lost: the full original is always offered in the UI):
// - a "Warum:" paragraph separated from its bullet by a blank line becomes a text block;
// - a line like "5. Mai ..." is read as a numbered bullet;
// - a bare URL in the middle of a sentence is removed from the sentence.

const DOMAIN_LIKE = /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;
const BULLET = /^\s*(?:[-•*–]|\d{1,2}[.)])\s+(\S.*)$/;
const LABEL = /^(?:warum|begründung|grund|why|reason|because)\s*:\s*/i;
// Broadened 2026-09-30 (owner report): a real answer's closing
// continuation-offer ("Wenn Sie mir sagen, was Ihnen wichtiger ist ..., kann
// ich ...") didn't match the original narrower list and rendered as a full-
// weight text block instead of the de-emphasized outro style.
const OUTRO = /^(?:soll ich|möchten sie|wenn sie (?:möchten|mir|uns)|sagen sie mir|lassen sie mich wissen|teilen sie mir|kann ich|would you like|shall i|can i|if you (?:like|tell me|share)|let me know)\b/i;
const TRAIL = ".,;:!?'\"";

const hostOf = (url) => {
  const m = /^[a-z][a-z0-9+.-]*:\/\/(?:[^/?#@]*@)?([^/?#:]+)/i.exec(url.trim());
  return m ? m[1].toLowerCase().replace(/^www\./, "") : null;
};

// Index just past the ")" that closes an already-open "(", honouring nested ones.
function closeParen(s, from) {
  let depth = 1;
  for (let i = from; i < s.length; i++) {
    if (s[i] === "(") depth++;
    else if (s[i] === ")" && --depth === 0) return i + 1;
  }
  return -1;
}

// Strips markdown links, bare URLs and emphasis marks from one string.
// Returns { text, sources } where sources are de-duplicated domains.
export function cleanText(input) {
  let s = String(input == null ? "" : input);
  const sources = [];
  const addSource = (d) => { if (d && !sources.includes(d)) sources.push(d); };
  let changed = false;

  // 1. [label](url) with balanced parentheses inside the url
  let out = "";
  let pos = 0;
  const re = /\[([^[\]]*)\]\(/g;
  let m;
  let lastDropEnd = -1;
  while ((m = re.exec(s))) {
    const urlStart = m.index + m[0].length;
    let end = closeParen(s, urlStart);
    let url;
    if (end === -1) {                       // unclosed: take the rest of the token
      const ws = s.slice(urlStart).search(/\s/);
      end = ws === -1 ? s.length : urlStart + ws;
      url = s.slice(urlStart, end);
    } else {
      url = s.slice(urlStart, end - 1);
    }
    const label = m[1].trim();
    const labelIsDomain = DOMAIN_LIKE.test(label);
    addSource(labelIsDomain ? label.toLowerCase().replace(/^www\./, "") : hostOf(url));
    out += s.slice(pos, m.index);
    // The label is citation syntax only inside a parenthesised citation;
    // in running text it is wording and stays.
    let drop = !label || /^https?:\/\//i.test(label);
    if (!drop && labelIsDomain) {
      const before = s.slice(0, m.index).trimEnd();
      const after = s.slice(end);
      const prevCh = before[before.length - 1];
      const nextCh = after.trimStart()[0];
      const chained = lastDropEnd >= 0 && /^\s*[,;]\s*$/.test(s.slice(lastDropEnd, m.index));
      const inParens = (prevCh === "(" || chained) && (nextCh === ")" || nextCh === "," || nextCh === ";");
      // a string that is nothing but the link is a bare citation, not a sentence
      const alone = /^(?:[-•*–]|\d{1,2}[.)])?$/.test(before) && /^[ \t]*[.,;:!?]*[ \t]*$/.test(after);
      drop = inParens || alone;
    }
    if (drop) lastDropEnd = end;
    out += drop ? "" : label;
    changed = true;
    pos = end;
    re.lastIndex = end;
  }
  s = out + s.slice(pos);

  // 2. bare URLs
  s = s.replace(/https?:\/\/[^\s<>"]+/gi, (u) => {
    let url = u;
    for (;;) {
      const last = url[url.length - 1];
      const unbalanced = (last === ")" && url.split(")").length > url.split("(").length) ||
        (last === "]" && url.split("]").length > url.split("[").length);
      if (TRAIL.includes(last) || unbalanced) url = url.slice(0, -1); else break;
    }
    addSource(hostOf(url));
    changed = true;
    return u.slice(url.length);
  });

  // 3. emphasis / heading marks (syntax only)
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/(^|[^\w])__([^_\n]+)__(?=[^\w]|$)/g, "$1$2").replace(/^#{1,6}\s+/gm, "");

  if (changed) {
    let prev;
    do { prev = s; s = s.replace(/\(\s*(?:[,;]\s*)*\)|\[\s*\]/g, ""); } while (s !== prev);
    s = s.replace(/\s+([.,;:!?])/g, "$1");
  }
  s = s.replace(/[ \t]{2,}/g, " ").trim();
  return { text: s, sources };
}

// Fallback view: cleaned paragraphs split on blank lines.
export function cleanParagraphs(raw) {
  if (typeof raw !== "string") return [];
  return raw.replace(/\r\n?/g, "\n").split(/\n\s*\n/)
    .map((p) => cleanText(p.split("\n").map((l) => l.trim()).join("\n")).text)
    .filter((p) => /[\p{L}\p{N}]/u.test(p));
}

const isHeading = (line, nextLine) => {
  if (!nextLine || !BULLET.test(nextLine) || BULLET.test(line)) return false;
  const t = cleanText(line).text.replace(/:$/, "").trim();
  return !!t && t.split(/\s+/).length <= 3 && !/[.?!]/.test(t);
};

const indentOf = (l) => /^[ \t]*/.exec(l)[0].replace(/\t/g, "  ").length;

// Ordered blocks in document order:
//   {type:"intro", text} {type:"item", n, title, body:[lines], sources}
//   {type:"note", title|null, lines} {type:"text", text} {type:"outro", text}
export function parseAiAnswer(raw) {
  const empty = { blocks: [], ok: false };
  if (typeof raw !== "string" || !raw.trim()) return empty;
  const lines = raw.replace(/\r\n?/g, "\n").split("\n");

  const introLines = [];
  const rawBlocks = [];
  let mode = "pre";           // pre | item | note | text
  let cur = null;
  let itemIndent = 0;
  let sawBlank = true;        // blank line since the previous content line (or start)

  const flushIntro = () => {
    if (!introLines.length) return;
    rawBlocks.push({ type: "intro", text: introLines.join("\n") });
    introLines.length = 0;
  };
  const open = (b, m) => { flushIntro(); cur = b; rawBlocks.push(b); mode = m; };
  const appendLast = (arr, t) => { if (arr.length) arr[arr.length - 1] += " " + t; else arr.push(t); };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) {
      sawBlank = true;
      if (mode === "pre" && introLines.length && introLines[introLines.length - 1] !== "") introLines.push("");
      continue;
    }
    const b = BULLET.exec(line);
    const t = line.trim();
    if (b) {
      if (mode === "note" && !sawBlank) {
        cur.lines.push(b[1].trim());
      } else if (mode === "item" && indentOf(line) > itemIndent) {
        cur.body.push(b[1].trim());
      } else {
        itemIndent = indentOf(line);
        open({ type: "item", title: b[1].trim(), body: [] }, "item");
      }
    } else if (mode === "pre") {
      introLines.push(t);
    } else if (!sawBlank && mode === "item") {
      appendLast(cur.body, t);
    } else if (!sawBlank && mode === "note") {
      appendLast(cur.lines, t);
    } else if (!sawBlank && mode === "text") {
      cur.text += " " + t;
    } else if (mode === "item" && LABEL.test(t)) {
      cur.body.push(t);
    } else if (isHeading(line, lines[i + 1])) {
      let nb = 0;
      while (BULLET.test(lines[i + 1 + nb] || "")) nb++;
      const heading = cleanText(line).text;
      if (/:$/.test(heading) || nb >= 2) open({ type: "text", text: heading }, "text");   // a list follows
      else open({ type: "note", title: heading.replace(/:$/, "").trim(), lines: [] }, "note");
    } else {
      open({ type: "text", text: t }, "text");
    }
    sawBlank = false;
  }
  flushIntro();

  const blocks = [];
  let n = 0;
  for (const rb of rawBlocks) {
    if (rb.type === "intro") {
      const text = rb.text.split(/\n\n+/).map((p) => cleanText(p.split("\n").join(" ")).text).filter(Boolean).join("\n\n");
      if (text) blocks.push({ type: "intro", text });
    } else if (rb.type === "item") {
      const t = cleanText(rb.title);
      const parts = rb.body.map((l) => cleanText(l.replace(LABEL, "")));
      let title = t.text;
      let body = parts.map((p) => p.text).filter(Boolean);
      if (!title && body.length) { title = body[0]; body = body.slice(1); }
      if (!title) continue;
      const sources = [];
      [t, ...parts].forEach((p) => p.sources.forEach((d) => { if (!sources.includes(d)) sources.push(d); }));
      blocks.push({ type: "item", n: ++n, title, body, sources });
    } else if (rb.type === "note") {
      const lns = rb.lines.map((l) => cleanText(l).text).filter(Boolean);
      if (lns.length) blocks.push({ type: "note", title: rb.title || null, lines: lns });
    } else {
      const text = cleanText(rb.text).text;
      if (text) blocks.push({ type: "text", text });
    }
  }

  // A continuation-offer at the start of the final block is a reliable
  // enough signal on its own — real content essentially never opens a final
  // sentence with "Kann ich ..."/"Wenn Sie mir ..." — so this no longer also
  // requires a trailing "?": the example above ends in "." and is still
  // exactly the filler this classification exists to de-emphasize.
  const last = blocks[blocks.length - 1];
  if (last && last.type === "text" && OUTRO.test(last.text)) last.type = "outro";

  return { blocks, ok: n > 0 };
}
